"use client";

/**
 * AlertDescriptionView — Description tab on /alerts/[id].
 *
 * Pure briefing. ALL config (schedule, email, visibility) now lives in the
 * "Edit details" modal — this tab is only the plain-English write-up of what
 * the workflow does and what the dashboard shows.
 *
 * The briefing is generated server-side by a vision model (it looks at the
 * latest dashboard) and lazily (re)generated on first view when stale. Because
 * a cold regen can take ~30-50s, we show a friendly "writing it up" state.
 */

import useSWR from "swr";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Clock, FileText, Loader2, Mail, Pencil, Sparkles } from "lucide-react";
import { apiClient } from "@/lib/api-client";

export function AlertDescriptionView({
  alertId,
  initialExplanationMd,
  scheduleText,
  notificationEmail,
  canEdit = false,
  viewMode,
}: {
  alertId: string;
  initialExplanationMd?: string | null;
  scheduleText?: string | null;
  notificationEmail?: string | null;
  canEdit?: boolean;
  viewMode?: string;
}) {
  const { data, isLoading } = useSWR(
    alertId && !initialExplanationMd ? `alert-explanation-${alertId}` : null,
    () => apiClient.getAlertExplanation(alertId),
    {
      revalidateOnFocus: false,
      dedupingInterval: 30_000,
      shouldRetryOnError: false,
    },
  );

  const explanation = (initialExplanationMd || data?.explanation_md || "").trim();
  const openEditDetails = () => {
    window.dispatchEvent(new CustomEvent("xynth-alert-open-edit-details"));
  };

  if (!initialExplanationMd && isLoading) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
        <div className="relative">
          <Sparkles className={`w-6 h-6 ${viewMode === "signal" ? "text-emerald-400" : "text-sky-400"}`} />
          <Loader2 className={`w-6 h-6 animate-spin absolute inset-0 ${viewMode === "signal" ? "text-emerald-400/40" : "text-sky-400/40"}`} />
        </div>
        <p className="text-[13px] text-muted-foreground max-w-xs leading-relaxed">
          Writing a plain-English summary of this alert…
          <br />
          <span className="text-[11.5px] text-muted-foreground/70">
            This can take a few moments the first time.
          </span>
        </p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto">
      <div className="mx-auto max-w-2xl px-5 md:px-8 py-8 md:py-10 flex flex-col gap-3.5">
        <div className="flex items-center gap-2 text-foreground/70">
          <FileText className={`w-3.5 h-3.5 ${viewMode === "signal" ? "text-emerald-300" : "text-sky-300"}`} />
          <span className="text-[10.5px] font-semibold uppercase tracking-[0.14em]">
            What this {viewMode === "signal" ? "signal" : "alert"} does
          </span>
        </div>

        {(scheduleText || notificationEmail) && (
          <div className="mb-2 grid gap-2 sm:grid-cols-2">
            {scheduleText && (
              <EditableFact
                icon={<Clock className="w-3.5 h-3.5" />}
                label="Runs"
                value={scheduleText}
                canEdit={canEdit}
                onEdit={openEditDetails}
                viewMode={viewMode}
              />
            )}
            {notificationEmail && (
              <EditableFact
                icon={<Mail className="w-3.5 h-3.5" />}
                label="Emails"
                value={notificationEmail}
                canEdit={canEdit}
                onEdit={openEditDetails}
                viewMode={viewMode}
              />
            )}
          </div>
        )}

        {explanation ? (
          <div
            className={[
              "prose prose-sm prose-invert max-w-none",
              "text-[14.5px] leading-[1.75] text-foreground/95",
              "[&_p]:my-0 [&_p+p]:mt-3.5",
              "[&_h2]:text-[17px] [&_h2]:font-semibold [&_h2]:text-foreground [&_h2]:mt-0 [&_h2]:mb-3",
              viewMode === "signal" ? "[&_h3]:text-[12px] [&_h3]:font-semibold [&_h3]:uppercase [&_h3]:tracking-[0.12em] [&_h3]:text-emerald-200 [&_h3]:mt-7 [&_h3]:mb-3" : "[&_h3]:text-[12px] [&_h3]:font-semibold [&_h3]:uppercase [&_h3]:tracking-[0.12em] [&_h3]:text-sky-200 [&_h3]:mt-7 [&_h3]:mb-3",
              "[&_ul]:mt-2.5 [&_ul]:mb-0 [&_ol]:mt-2.5 [&_ol]:mb-0 [&_ul]:pl-5 [&_ol]:pl-5",
              viewMode === "signal" ? "[&_li]:my-1.5 [&_li]:leading-[1.6] [&_li]:marker:text-emerald-400/70 [&_li]:pl-1" : "[&_li]:my-1.5 [&_li]:leading-[1.6] [&_li]:marker:text-sky-400/70 [&_li]:pl-1",
              "[&_strong]:text-foreground [&_strong]:font-semibold",
              viewMode === "signal" ? "[&_code]:bg-muted [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-[12px] [&_code]:text-emerald-300 [&_code]:font-normal" : "[&_code]:bg-muted [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-[12px] [&_code]:text-sky-300 [&_code]:font-normal",
            ].join(" ")}
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{explanation}</ReactMarkdown>
          </div>
        ) : (
          <p className="text-[13px] text-muted-foreground italic">
            No description yet. It will be written automatically after this alert
            runs.
          </p>
        )}
      </div>
    </div>
  );
}

function EditableFact({
  icon,
  label,
  value,
  canEdit,
  onEdit,
  viewMode,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  canEdit: boolean;
  onEdit: () => void;
  viewMode?: string;
}) {
  return (
    <div className="group flex min-w-0 items-center gap-2 rounded-lg border border-slate-300/10 bg-slate-950/35 px-3 py-2">
      <span className={`shrink-0 ${viewMode === "signal" ? "text-emerald-300/90" : "text-sky-300/90"}`}>{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="text-[9.5px] font-semibold uppercase tracking-[0.16em] text-slate-400">
          {label}
        </div>
        <div className="truncate text-[12.5px] leading-snug text-slate-100/95">
          {value}
        </div>
      </div>
      {canEdit && (
        <button
          type="button"
          aria-label={`Edit ${label.toLowerCase()}`}
          title="Edit details"
          onClick={onEdit}
          className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors ${viewMode === "signal" ? "hover:bg-emerald-400/10 hover:text-emerald-200" : "hover:bg-sky-400/10 hover:text-sky-200"}`}
        >
          <Pencil className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}
